/**
 * Certificate and badge generation utilities.
 * Generates downloadable PDF certificates and PNG badges client-side
 * using canvas rendering.
 */

import type { CertificationProgram } from "./certifications";

export interface CertificateData {
  recipientName: string;
  programTitle: string;
  programCode: string;
  industry: string;
  level: string;
  issueDate: string;
  expiryDate: string;
  verificationCode: string;
  companyLogoUrl?: string;
  companyName?: string;
}

export interface BadgeData {
  recipientName: string;
  programTitle: string;
  programCode: string;
  industry: string;
  level: string;
  issueDate: string;
  verificationCode: string;
}

const LEVEL_COLORS: Record<string, string> = {
  Professional: "#2563eb",
  Supervisor: "#0891b2",
  Manager: "#d97706",
  Executive: "#7c3aed",
};

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return dateStr;
  }
}

export function generateCertificatePDF(data: CertificateData): void {
  const canvas = document.createElement("canvas");
  const width = 1200;
  const height = 850;
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Load company logo if provided, then render
  const renderCertificate = (companyLogo: HTMLImageElement | null) => {
  // Background
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  // Border
  ctx.strokeStyle = "#1e3a5f";
  ctx.lineWidth = 8;
  ctx.strokeRect(20, 20, width - 40, height - 40);

  // Inner border
  ctx.strokeStyle = "#d4af37";
  ctx.lineWidth = 2;
  ctx.strokeRect(40, 40, width - 80, height - 80);

  // Company logo (top-left) if provided
  if (companyLogo && data.companyLogoUrl) {
    const logoMaxW = 140;
    const logoMaxH = 70;
    const ratio = Math.min(logoMaxW / companyLogo.width, logoMaxH / companyLogo.height);
    const lw = companyLogo.width * ratio;
    const lh = companyLogo.height * ratio;
    ctx.drawImage(companyLogo, 60, 55, lw, lh);
  }

  // Company name (top-right) if provided
  if (data.companyName) {
    ctx.fillStyle = "#475569";
    ctx.font = "bold 16px Georgia, serif";
    ctx.textAlign = "right";
    ctx.fillText(data.companyName, width - 60, 80);
  }

  // Header
  ctx.fillStyle = "#1e3a5f";
  ctx.font = "bold 48px Georgia, serif";
  ctx.textAlign = "center";
  ctx.fillText("SENTINEL", width / 2, 120);

  ctx.fillStyle = "#64748b";
  ctx.font = "20px Georgia, serif";
  ctx.fillText("Certified Safety Architecture Professional", width / 2, 155);

  // Decorative line
  ctx.strokeStyle = "#d4af37";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(width / 2 - 200, 180);
  ctx.lineTo(width / 2 + 200, 180);
  ctx.stroke();

  // Certificate title
  ctx.fillStyle = "#0f172a";
  ctx.font = "bold 36px Georgia, serif";
  ctx.fillText("Certificate of Completion", width / 2, 240);

  // Recipient
  ctx.fillStyle = "#64748b";
  ctx.font = "22px Georgia, serif";
  ctx.fillText("This certifies that", width / 2, 300);

  ctx.fillStyle = "#1e3a5f";
  ctx.font = "bold 42px Georgia, serif";
  ctx.fillText(data.recipientName, width / 2, 360);

  // Decorative line under name
  ctx.strokeStyle = "#d4af37";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(width / 2 - 250, 380);
  ctx.lineTo(width / 2 + 250, 380);
  ctx.stroke();

  // Program info
  ctx.fillStyle = "#64748b";
  ctx.font = "22px Georgia, serif";
  ctx.fillText("has successfully completed the", width / 2, 420);

  ctx.fillStyle = "#0f172a";
  ctx.font = "bold 28px Georgia, serif";
  ctx.fillText(data.programTitle, width / 2, 460);

  // Industry and level
  ctx.fillStyle = "#475569";
  ctx.font = "20px Georgia, serif";
  ctx.fillText(`${data.industry}  |  ${data.level} Level  |  Code: ${data.programCode}`, width / 2, 500);

  // Level color bar
  const levelColor = LEVEL_COLORS[data.level] ?? "#2563eb";
  ctx.fillStyle = levelColor;
  ctx.fillRect(width / 2 - 100, 520, 200, 4);

  // Dates
  ctx.fillStyle = "#64748b";
  ctx.font = "18px Georgia, serif";
  ctx.fillText(`Issued: ${formatDate(data.issueDate)}`, width / 2 - 150, 580);
  ctx.fillText(`Valid Until: ${formatDate(data.expiryDate)}`, width / 2 + 150, 580);

  // Verification code
  ctx.fillStyle = "#94a3b8";
  ctx.font = "14px monospace";
  ctx.fillText(`Verification Code: ${data.verificationCode}`, width / 2, 630);

  // Signatures
  ctx.fillStyle = "#1e3a5f";
  ctx.font = "italic 20px Georgia, serif";
  ctx.fillText("Sentinel Certification Authority", width / 2 - 200, 720);
  ctx.fillText("Program Director", width / 2 + 200, 720);

  ctx.strokeStyle = "#1e3a5f";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(width / 2 - 300, 700);
  ctx.lineTo(width / 2 - 100, 700);
  ctx.moveTo(width / 2 + 100, 700);
  ctx.lineTo(width / 2 + 300, 700);
  ctx.stroke();

  // Seal
  ctx.beginPath();
  ctx.arc(width / 2, 760, 40, 0, Math.PI * 2);
  ctx.fillStyle = "#d4af37";
  ctx.fill();
  ctx.strokeStyle = "#1e3a5f";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = "#1e3a5f";
  ctx.font = "bold 14px Georgia, serif";
  ctx.textAlign = "center";
  ctx.fillText("S", width / 2, 765);

  // Download
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Sentinel-Certificate-${data.programCode}-${data.verificationCode}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, "image/png");
  }; // end renderCertificate

  // If company logo provided, load it first then render
  if (data.companyLogoUrl) {
    const logo = new Image();
    logo.crossOrigin = "anonymous";
    logo.onload = () => renderCertificate(logo);
    logo.onerror = () => renderCertificate(null);
    logo.src = data.companyLogoUrl;
  } else {
    renderCertificate(null);
  }
}

export function generateBadgePNG(data: BadgeData): void {
  const canvas = document.createElement("canvas");
  const size = 512;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const levelColor = LEVEL_COLORS[data.level] ?? "#2563eb";

  // Background circle
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 10, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();

  // Outer ring
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 10, 0, Math.PI * 2);
  ctx.strokeStyle = levelColor;
  ctx.lineWidth = 8;
  ctx.stroke();

  // Inner ring
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 30, 0, Math.PI * 2);
  ctx.strokeStyle = "#d4af37";
  ctx.lineWidth = 2;
  ctx.stroke();

  // Load Sentinel logo and draw it, then render the rest
  const logo = new Image();
  logo.onload = () => {
    // Draw logo centered at top
    const logoSize = 80;
    ctx.drawImage(logo, size / 2 - logoSize / 2, 70, logoSize, logoSize);

    // Sentinel text below logo
    ctx.fillStyle = "#1e3a5f";
    ctx.font = "bold 28px Georgia, serif";
    ctx.textAlign = "center";
    ctx.fillText("SENTINEL", size / 2, 175);

    // Level badge
    ctx.fillStyle = levelColor;
    ctx.beginPath();
    ctx.roundRect(size / 2 - 80, 195, 160, 36, 18);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 18px Georgia, serif";
    ctx.fillText(data.level, size / 2, 219);

    // Industry
    ctx.fillStyle = "#475569";
    ctx.font = "16px Georgia, serif";
    ctx.fillText(data.industry, size / 2, 265);

    // Program code
    ctx.fillStyle = "#1e3a5f";
    ctx.font = "bold 24px monospace";
    ctx.fillText(data.programCode, size / 2, 310);

    // Recipient
    ctx.fillStyle = "#64748b";
    ctx.font = "14px Georgia, serif";
    ctx.fillText("Awarded to", size / 2, 350);

    ctx.fillStyle = "#0f172a";
    ctx.font = "bold 20px Georgia, serif";
    ctx.fillText(data.recipientName, size / 2, 380);

    // Date
    ctx.fillStyle = "#94a3b8";
    ctx.font = "12px Georgia, serif";
    ctx.fillText(formatDate(data.issueDate), size / 2, 415);

    // Verification
    ctx.fillStyle = "#cbd5e1";
    ctx.font = "10px monospace";
    ctx.fillText(data.verificationCode, size / 2, 445);

    // Star decoration
    ctx.fillStyle = "#d4af37";
    drawStar(ctx, size / 2, 475, 5, 18, 9);

    // Download
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sentinel-Badge-${data.programCode}-${data.verificationCode}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, "image/png");
  };
  logo.onerror = () => {
    // Fallback: draw text logo if image fails
    ctx.fillStyle = "#1e3a5f";
    ctx.font = "bold 32px Georgia, serif";
    ctx.textAlign = "center";
    ctx.fillText("SENTINEL", size / 2, 120);

    ctx.fillStyle = levelColor;
    ctx.beginPath();
    ctx.roundRect(size / 2 - 80, 140, 160, 36, 18);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 18px Georgia, serif";
    ctx.fillText(data.level, size / 2, 164);

    ctx.fillStyle = "#475569";
    ctx.font = "16px Georgia, serif";
    ctx.fillText(data.industry, size / 2, 210);

    ctx.fillStyle = "#1e3a5f";
    ctx.font = "bold 24px monospace";
    ctx.fillText(data.programCode, size / 2, 260);

    ctx.fillStyle = "#64748b";
    ctx.font = "14px Georgia, serif";
    ctx.fillText("Awarded to", size / 2, 310);

    ctx.fillStyle = "#0f172a";
    ctx.font = "bold 20px Georgia, serif";
    ctx.fillText(data.recipientName, size / 2, 340);

    ctx.fillStyle = "#94a3b8";
    ctx.font = "12px Georgia, serif";
    ctx.fillText(formatDate(data.issueDate), size / 2, 380);

    ctx.fillStyle = "#cbd5e1";
    ctx.font = "10px monospace";
    ctx.fillText(data.verificationCode, size / 2, 420);

    ctx.fillStyle = "#d4af37";
    drawStar(ctx, size / 2, 460, 5, 20, 10);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sentinel-Badge-${data.programCode}-${data.verificationCode}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, "image/png");
  };
  logo.src = "/sentinel_logo.png";
}

function drawStar(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  spikes: number,
  outerRadius: number,
  innerRadius: number,
): void {
  let rot = (Math.PI / 2) * 3;
  let x = cx;
  let y = cy;
  const step = Math.PI / spikes;

  ctx.beginPath();
  ctx.moveTo(cx, cy - outerRadius);
  for (let i = 0; i < spikes; i++) {
    x = cx + Math.cos(rot) * outerRadius;
    y = cy + Math.sin(rot) * outerRadius;
    ctx.lineTo(x, y);
    rot += step;

    x = cx + Math.cos(rot) * innerRadius;
    y = cy + Math.sin(rot) * innerRadius;
    ctx.lineTo(x, y);
    rot += step;
  }
  ctx.lineTo(cx, cy - outerRadius);
  ctx.closePath();
  ctx.fill();
}

export function buildCertificateData(
  recipientName: string,
  program: CertificationProgram,
  verificationCode: string,
  issueDate: string,
  expiryDate: string,
  companyLogoUrl?: string,
  companyName?: string,
): CertificateData {
  return {
    recipientName,
    programTitle: program.title,
    programCode: program.code,
    industry: program.industry,
    level: program.level,
    issueDate,
    expiryDate,
    verificationCode,
    companyLogoUrl,
    companyName,
  };
}

export function buildBadgeData(
  recipientName: string,
  program: CertificationProgram,
  verificationCode: string,
  issueDate: string,
): BadgeData {
  return {
    recipientName,
    programTitle: program.title,
    programCode: program.code,
    industry: program.industry,
    level: program.level,
    issueDate,
    verificationCode,
  };
}
